import React, { useEffect, useRef, useState } from 'react'
import { getFilteredCodes, updateFinanceCode, getFinanceCodes, addFinanceCodeBatch } from '../../services/finance-service';
import CustomBreadcrumb from '../common/CustomBreadcrumb';
import Alert from '../common/Alert';
import { Button, Col, Container, Row, Spinner, ProgressBar } from 'react-bootstrap';
import ResponsiveTable from '../Grid/ResponsiveTable';
import FinanceForm from './FinanceForm';
import deleteIcon from '../../assets/delete.svg';
import editIcon from '../../assets/edit.svg';
import InputBox from '../common/InputBox';
import SearchSelect from '../common/SearchSelect';
import withReactContent from "sweetalert2-react-content";
import Swal from 'sweetalert2';
import constants from "../../helpers/constants";
import { formatDateTime } from "../../helpers/date-formatter";
import Loader from '../common/loader';
import { validateAlphaNumeric } from '../../helpers/validations';

export default function FinanceMaster() {

  const MySwal = withReactContent(Swal);
  const [action, setAction] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [rowData, setRowData] = useState({});
  const [reload, setReload] = useState(false);
  const [data, setData] = useState([]);
  const dataRef = useRef([]);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(() => {
    return localStorage.getItem('financeUploadLoading') === 'true';
  });

  // Pagination states - EXACTLY match Report Log pattern
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 1000, // Match Report Log default
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false
  });
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadingAll, setLoadingAll] = useState(false);

  const setUploadLoadingWithStorage = (loading) => {
    setUploadLoading(loading);
    localStorage.setItem('financeUploadLoading', loading.toString());
  };

  const dstAssociation = ["Single", "DMDC"];
  const [financeObj, setFinanceObj] = useState({
    vendorCode: "",
    gadCustomerCode: "",
    lob: "",
    financeCode: "",
    retailerGstin: "",
    retailerName: "",
    dstAssociation: "",
    status: "Active",
  });

  const handleReset = () => {
    setFinanceObj({
      vendorCode: "",
      gadCustomerCode: "",
      lob: "",
      financeCode: "",
      retailerGstin: "",
      retailerName: "",
      dstAssociation: "",
      status: "Active",
    });
    setData([]);
    setPagination({
      page: 1,
      limit: 1000, // Match Report Log
      total: 0,
      totalPages: 0,
      hasNext: false,
      hasPrev: false
    });
  }
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFinanceObj((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  }

  const [headers] = useState([
    { displayName: "Status", columnName: "status", dataType: "text", width: 180, maxLength:50, displayIndication: true, required: true, switch: sessionStorage.getItem("Role") === "Admin", setStatus: setStatus, upload: true },
    { displayName: "Vendor Code", columnName: "vendorCode", dataType: "text", width: 150, maxLength:50, displayIndication: true, required: true, upload: true },
    { displayName: "GAD Customer Code", columnName: "gadCustomerCode", dataType: "text", width: 200, maxLength:50, displayIndication: true, required: true, upload: true },
    { displayName: "LOB", columnName: "lob", dataType: "text", width: 120, maxLength:50, displayIndication: true, required: true, upload: true },
    { displayName: "Finance Code", columnName: "financeCode", dataType: "text", width: 160, maxLength:50, displayIndication: true, required: true, upload: true },
    { displayName: "Retailer GSTIN", columnName: "retailerGstin", dataType: "text", width: 180, maxLength:50, displayIndication: true, required: true, upload: true },
    { displayName: "Retailer Name", columnName: "retailerName", dataType: "text", width: 180, maxLength:50, displayIndication: true, required: true, upload: true },
    { displayName: "DST Association", columnName: "dstAssociation", dataType: "text", width: 180, maxLength:50, displayIndication: true, required: true, upload: true },
    { displayName: "Updated On", columnName: "createdOn", dataType: "date", width: 180, displayIndication: true, formatter: formatDateTime },
    { displayName: "Updated By", columnName: "createdById", dataType: "text", width: 160, displayIndication: true },
  ]);

  const handleAdd = () => {
    setAction("Add");
    setShowForm(true);
  };

  useEffect(() => {
    getFinance();
  }, [reload]);

  useEffect(() => {
    const checkUploadStatus = () => {
      const storageValue = localStorage.getItem('financeUploadLoading') === 'true';
      if (storageValue !== uploadLoading) {
        setUploadLoading(storageValue);
      }
    };
    
    const interval = setInterval(checkUploadStatus, 100);
    return () => clearInterval(interval);
  }, [uploadLoading]);

  // Simple get finance data (backward compatible)
  const getFinance = async () => {
    setLoading(true)
    try {
      let result = await getFinanceCodes();
      if (result) {
        setData(result)
        dataRef.current = result;
        setPagination(prev => ({ ...prev, total: result.length, totalPages: 1, hasNext: false, hasPrev: false }));
      }
    } catch (error) {
      console.error('Error fetching finance codes:', error);
      Alert("error", error.response?.data?.message || "Error fetching finance codes");
    }
    setLoading(false)
  }

  // Enhanced filtered codes with pagination support - matches Report Log pattern
  const getFilteredFinanceCodes = async (page = 1, isLoadMore = false) => {
    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setData([]);
    }
    
    const isUserObjEmpty = Object.values(financeObj).every(value => value === "" || value === "Active");
    
    try {
      let result;
      if (isUserObjEmpty) {
        // If no filters, use old API for compatibility
        result = await getFinanceCodes();
        if (result) {
          setData(result);
        }
      } else {
        // Use filtered codes with pagination - EXACTLY like Report Log
        const payload = {
          ...financeObj,
          page,
          limit: 1000 // Match Report Log default
        };
        result = await getFilteredCodes(payload);
        
        if (result && result.success) {
          if (page === 1) {
            setData(result.data);
          } else {
            setData(prevData => [...prevData, ...result.data]);
          }
          setPagination(result.pagination);
        } else if (result && Array.isArray(result)) {
          // Handle old API response format
          setData(result);
        } else {
          Alert("error", "Failed to fetch finance data");
        }
      }
      
      if ((result && result.length === 0) || (result && result.data && result.data.length === 0)) {
        Alert("info", "No data found");
      }
      
    } catch (error) {
      console.error('Error fetching filtered finance codes:', error);
      Alert("error", error.response?.data?.message || "Error fetching finance data");
    }
    
    setLoading(false);
    setLoadingMore(false);
  }
  
  // Load All functionality - same pattern as Validation Log Report
  const getAllFinanceData = async () => {
    if (loadingAll) return;
    
    setLoadingAll(true);
    setData([]);
    
    try {
      const firstPagePayload = {
        ...financeObj,
        page: 1,
        limit: 1000
      };
      
      const firstResult = await getFilteredCodes(firstPagePayload);
      
      if (!firstResult || !firstResult.success) {
        Alert("error", "Failed to fetch finance data");
        setLoadingAll(false);
        return;
      }
      
      if (firstResult.data.length === 0) {
        Alert("info", "No data found");
        setLoadingAll(false);
        return;
      }
      
      const allData = [...firstResult.data];
      setData(allData);
      
      if (firstResult.pagination.hasNext) {
        const totalPages = firstResult.pagination.totalPages;
        
        const batchSize = 3;
        for (let startPage = 2; startPage <= totalPages; startPage += batchSize) {
          const endPage = Math.min(startPage + batchSize - 1, totalPages);
          const pagePromises = [];
          
          for (let page = startPage; page <= endPage; page++) {
            pagePromises.push(getFilteredCodes({ ...financeObj, page, limit: 1000 }));
          }
          
          try {
            const batchResults = await Promise.all(pagePromises);
            batchResults.forEach(result => {
              if (result && result.success) {
                allData.push(...result.data);
              }
            });
            setData([...allData]);
            
            if (endPage < totalPages) {
              await new Promise(resolve => setTimeout(resolve, 300));
            }
          } catch (batchError) {
            console.error('Error in batch:', batchError);
          }
        }
      }
      
      setPagination({
        page: 1,
        limit: allData.length,
        total: allData.length,
        totalPages: 1,
        hasNext: false,
        hasPrev: false
      });
      Alert("success", `Loaded all ${allData.length.toLocaleString()} records successfully`);
      
    } catch (error) {
      console.error('Error loading all finance data:', error);
      Alert("error", error.response?.data?.message || "Error loading all finance data");
    }
    setLoadingAll(false);
  }
  
  // Load More functionality
  const handleLoadMore = () => {
    if (pagination.hasNext) {
      getFilteredFinanceCodes(pagination.page + 1, true);
    }
  }
  
  function setStatus(item) {
    const userRole = sessionStorage.getItem("Role");
    if (userRole !== "Admin") {
      Alert("error", "Only Admin users can change the status");
      return;
    }
    
    console.log("setStatus item", item)
    setLoading(true);
    let result = updateFinanceCode(item);
    result.then(async (resp) => {
      Alert("success", "Status updated successfully");
      await getFinance();
      setLoading(false);
    }).catch((error) => {
      Alert("error", error);
      setLoading(false);
    })
  }

  const handleUpload = async (data) => {
    try {
      const result = await addFinanceCodeBatch(data);
      setUploadResult(result);
      setReload(!reload);
      return result;
    } catch (error) {
      throw error;
    }
  };

  let icons = [{ src: editIcon, click: edit }]

  function edit(item) {
    setAction("Edit");
    setRowData(item);
    setShowForm(true);
  }

  const commonProps = { onChange: handleChange }

  return (
    <>
        {loading && <Loader/>}      

      {showForm && (
        <FinanceForm
          show={showForm}
          action={action}
          rowdata={rowData}
          setReload={() => setReload(!reload)}
          financedata={data}
          dstAssociation={dstAssociation}
          onHide={() => setShowForm(false)}
        />
      )}

      <CustomBreadcrumb parentMenu={"Maintenance"} menu={"Tagging"}> </CustomBreadcrumb>
      <Container bsPrefix="main-body drop-down">
        <div className="page-title">Finance Master</div>
        <Row>
          <Col lg={3} md={12} className="txt-input">
            <InputBox type="text" label="Vendor Code" name="vendorCode" value={financeObj.vendorCode} customValidation= {validateAlphaNumeric} {...commonProps} />
          </Col>
          <Col lg={3} md={12} className="txt-input">
            <InputBox type="text" label="GAD Customer Code" name="gadCustomerCode" value={financeObj.gadCustomerCode} customValidation= {validateAlphaNumeric} {...commonProps} />
          </Col>
          <Col lg={3} md={12} className="txt-input">
            <InputBox type="text" label="LOB" name="lob" value={financeObj.lob} customValidation= {validateAlphaNumeric} {...commonProps} />
          </Col>
          <Col lg={3} md={12} className="txt-input">
            <InputBox type="text" label="Finance Code" name="financeCode" value={financeObj.financeCode} customValidation= {validateAlphaNumeric} {...commonProps} />
          </Col>
        </Row>
        <Row className='mb-2'>
          <Col lg={3} md={12} className="txt-input">
            <InputBox type="text" label="Retailer GSTIN" name="retailerGstin" value={financeObj.retailerGstin} customValidation= {validateAlphaNumeric} {...commonProps} />
          </Col>
          <Col lg={3} md={12} className="txt-input">
            <InputBox type="text" label="Retailer Name" name="retailerName" value={financeObj.retailerName} customValidation= {validateAlphaNumeric} {...commonProps} />
          </Col>
          <Col lg={3} md={12} className="txt-input">
            <SearchSelect
              data={dstAssociation}
              name="dstAssociation"
              value={financeObj.dstAssociation}
              label="DST Association"
              optional={true}
              {...commonProps}
            />
          </Col>
          <Col className="display-action-btn">
            <Button onClick={handleReset} className="btn btn-outline-primary reset-button" variant="outline-primary">Reset</Button>
            <Button onClick={() => getFilteredFinanceCodes(1, false)} className="btn btn-primary btn-color btn-go">Go</Button>
            <Button onClick={getAllFinanceData} className="btn btn-primary btn-color" disabled={loadingAll || loading}>
              {loadingAll ? 'Loading All...' : 'Load All'}
            </Button>
          </Col>
        </Row>
        
        {/* Show pagination info and Load More Button */}
        {data.length > 0 && (
          <Row className="mb-2">
            <Col lg={12}>
              <div className="pagination-info" style={{ 
                fontSize: '14px', 
                color: '#6c757d',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '10px',
                backgroundColor: '#f8f9fa',
                borderRadius: '5px'
              }}>
                <span>
                  Showing {data.length.toLocaleString()} of {pagination.total?.toLocaleString() || data.length.toLocaleString()} finance records 
                  {pagination.totalPages > 1 && `(Page ${pagination.page} of ${pagination.totalPages})`}
                </span>
                <div>
                  {pagination.hasNext && (
                    <Button 
                      variant="outline-primary" 
                      size="sm" 
                      onClick={handleLoadMore}
                      disabled={loadingMore}
                      className="me-2"
                    >
                      {loadingMore ? 'Loading More...' : 'Load More'}
                    </Button>
                  )}
                  {/* Always show Load All button like Report Log */}
                  <Button 
                    variant="outline-secondary" 
                    size="sm" 
                    disabled={loadingAll || loading}
                  >
                    {loadingAll ? 'Loading All...' : 'Load All'}
                  </Button>
                </div>
              </div>
            </Col>
          </Row>
        )}
        
        <ResponsiveTable 
          headers={headers} 
          data={data} 
          icons={icons} 
          DisplayExcelDownload={true} 
          dataType={"Finance"} 
          button={"New Finance"} 
          handleAdd={handleAdd} 
          sessionName={"Tagging"} 
          handleUpload={addFinanceCodeBatch} 
          setReload={setReload} 
          reload={reload} 
          pageSize={50}></ResponsiveTable>
      </Container>
    </>
  )
}
